import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ICreatePolicyMap, IPolicy, IPolicyMap, IScimGroup } from '@shared/models/scim-admin';
import { NotificationService } from '@shared/services/notification.service';
import { UserService } from '@shared/services/user.service';
import { forkJoin, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ScimAdminService } from '../scim-admin.service';

export interface ICreatePolicyMapDialogData {
	groups: IScimGroup[];
	policies: IPolicy[];
}

@Component({
	selector: 'app-create-policy-map-dialog',
	standalone: false,
	templateUrl: './create-policy-map-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./create-policy-map-dialog.component.scss'],
})
export class CreatePolicyMapDialogComponent implements OnInit, OnDestroy {
	@Output() created = new EventEmitter<IPolicyMap>();
	private destroy$ = new Subject<void>();

	form: FormGroup;
	isLoading = false;
	scimGroups: IScimGroup[] = [];
	policies: IPolicy[] = [];

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: ICreatePolicyMapDialogData,
		public dialogRef: MatDialogRef<CreatePolicyMapDialogComponent>,
		private scimAdminService: ScimAdminService,
		private userService: UserService,
		private fb: FormBuilder,
		private notificationService: NotificationService,
	) {}

	ngOnInit(): void {
		this.initializeForm();

		if (this.data.groups.length > 0 && this.data.policies.length > 0) {
			this.scimGroups = this.data.groups.filter((g, i, arr) => arr.findIndex((x) => x.id === g.id) === i);
			this.policies = this.data.policies;
		} else {
			this.loadData();
		}
	}

	private loadData(): void {
		forkJoin({
			groups: this.scimAdminService.getGroups(),
			managedPolicies: this.scimAdminService.getPolicies(),
			orgPolicies: this.scimAdminService.getOrganizationPolicies(),
			accountPolicies: this.scimAdminService.getAccountPolicies(),
		})
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: ({ groups, managedPolicies, orgPolicies, accountPolicies }) => {
					this.scimGroups = groups.filter((g, i, arr) => arr.findIndex((x) => x.id === g.id) === i);
					const allPoliciesMap = new Map<string, IPolicy>();
					managedPolicies.forEach((p) => allPoliciesMap.set(p.id, p));
					orgPolicies.forEach((p) => {
						if (!allPoliciesMap.has(p.id)) allPoliciesMap.set(p.id, p);
					});
					accountPolicies.forEach((p) => {
						if (!allPoliciesMap.has(p.id)) allPoliciesMap.set(p.id, p);
					});
					this.policies = Array.from(allPoliciesMap.values());
				},
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private initializeForm(): void {
		this.form = this.fb.group({
			groupExternalId: ['', [Validators.required]],
			policyId: ['', [Validators.required]],
		});
	}

	onCancel = () => this.dialogRef.close();

	onSave(): void {
		if (this.isLoading || this.form.invalid) return;

		this.isLoading = true;

		const data: ICreatePolicyMap = {
			organizationId: this.userService.account.organization.id,
			accountId: this.userService.account.id,
			groupExternalId: this.form.value.groupExternalId,
			policyId: this.form.value.policyId,
		};

		this.scimAdminService
			.createPolicyMap(data)
			.pipe(take(1))
			.subscribe({
				next: (createdPolicyMap) => {
					this.notificationService.notify(['scim-administration.notifications.createPolicyMap.header'], ['scim-administration.notifications.createPolicyMap.content'], 'success');
					this.created.emit(createdPolicyMap);
					this.dialogRef.close(createdPolicyMap);
				},
				error: (error) => {
					this.isLoading = false;
					console.error('Create policy map error:', error?.error || error);
					this.notificationService.notify(['scim-administration.errors.createPolicyMap.header'], [error?.error?.error?.message || 'scim-administration.errors.createPolicyMap.content'], 'error');
				},
			});
	}
}
